import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import NativeBannerAd from "@/components/ads/NativeBannerAd";

const Skeleton = () => (
  <div className="aspect-video w-full skeleton rounded-sm" />
);

const SectionHeader = ({ eyebrow, title, href, testId }) => (
  <div className="mb-6 flex items-end justify-between gap-3 md:mb-8 md:gap-4">
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#666] md:text-xs">
        {eyebrow}
      </div>
      <h2
        data-testid={testId}
        className="mt-1 font-display text-2xl tracking-tight text-[#f2f2f2] sm:text-3xl md:mt-2 md:text-4xl"
      >
        {title}
      </h2>
    </div>
    {href && (
      <Link
        to={href}
        data-testid={`${testId}-view-more`}
        className="group inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#f2f2f2] transition-colors hover:border-[#e60039] hover:bg-[#1a1a1a] hover:text-[#e60039] md:gap-2 md:px-4 md:text-xs"
      >
        <span className="hidden sm:inline">View More</span>
        <span className="sm:hidden">More</span>
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </Link>
    )}
  </div>
);

export const Home = () => {
  const [latest, setLatest] = useState(null);
  const [categories, setCategories] = useState([]);
  const [byCat, setByCat] = useState({});

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api
      .get("/videos", { params: { sort: "latest", limit: 2 } })
      .then((r) => setLatest(r.data.items));
  }, []);

  useEffect(() => {
    categories.forEach((c) => {
      api
        .get("/videos", { params: { category: c.slug, sort: "latest", limit: 2 } })
        .then((r) => setByCat((prev) => ({ ...prev, [c.slug]: r.data.items })));
    });
  }, [categories]);

  return (
    <div className="mx-auto max-w-7xl px-4 pt-6 md:px-12 md:pt-16">
      {/* LATEST */}
      <section id="latest" className="mb-12 animate-fade-up md:mb-20">
        <SectionHeader
          eyebrow="Just Dropped"
          title="Latest Videos"
          href="/browse?sort=latest"
          testId="section-latest"
        />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 md:gap-10">
          {!latest && Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} />)}
          {latest &&
            latest.map((v) => <VideoCard key={v.id} video={v} featured />)}
          {latest && latest.length === 0 && (
            <div className="col-span-full rounded-sm border border-dashed border-[#1a1a1a] p-10 text-center text-[#666]">
              No videos yet. Add some from the admin panel.
            </div>
          )}
        </div>
      </section>

      {/* CATEGORIES */}
      {categories.map((c, idx) => (
        <div key={c.slug}>
          {idx === 1 && <NativeBannerAd />}
          <section className="mb-12 md:mb-20">
            <SectionHeader
              eyebrow="Category"
              title={c.name}
              href={`/category/${c.slug}`}
              testId={`section-cat-${c.slug}`}
            />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 md:gap-10">
              {!byCat[c.slug] &&
                Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} />)}
              {byCat[c.slug] &&
                byCat[c.slug].map((v) => <VideoCard key={v.id} video={v} featured />)}
              {byCat[c.slug] && byCat[c.slug].length === 0 && (
                <div className="col-span-full rounded-sm border border-dashed border-[#1a1a1a] p-8 text-center text-sm text-[#666]">
                  Nothing in this category yet.
                </div>
              )}
            </div>
          </section>
        </div>
      ))}

      {/* BROWSE ALL CTA */}
      <section className="mb-16 flex justify-center md:mb-24">
        <Link
          to="/browse"
          data-testid="cta-browse-all"
          className="inline-flex items-center gap-2 rounded-sm bg-[#e60039] px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#b3002c] md:px-8 md:py-4"
        >
          Browse All
          <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </div>
  );
};

export default Home;
